import { useCallback, useRef } from 'react';
import { FaLock, FaLockOpen } from 'react-icons/fa';
import { DistributionEntry } from '../../../types';
import { colors } from '../../../../../colors';
import { formatNumber } from '../../../../../helpers/NumberFormatter';
import AssetIcon from '../../../../../components/AssetIcon';
import DistributionInputs from '../DistributionInputs';
import './index.css';

interface Props {
  entries: DistributionEntry[];
  onPercentageChange: (subscriptionId: number, pct: number) => void;
  onTotalChange: (subscriptionId: number, value: number) => void;
  onStableChange: (subscriptionId: number, value: number) => void;
  onAssetChange: (subscriptionId: number, value: number) => void;
  onToggleLock: (subscriptionId: number) => void;
}

const CardGauges = ({
  entries,
  onPercentageChange,
  onTotalChange,
  onStableChange,
  onAssetChange,
  onToggleLock,
}: Props) => {
  return (
    <div className="card-gauge-container">
      {entries.map((entry) => (
        <GaugeCard
          key={entry.subscriptionId}
          entry={entry}
          onPercentageChange={onPercentageChange}
          onTotalChange={onTotalChange}
          onStableChange={onStableChange}
          onAssetChange={onAssetChange}
          onToggleLock={onToggleLock}
        />
      ))}
    </div>
  );
};

interface CardProps {
  entry: DistributionEntry;
  onPercentageChange: (subscriptionId: number, pct: number) => void;
  onTotalChange: (subscriptionId: number, value: number) => void;
  onStableChange: (subscriptionId: number, value: number) => void;
  onAssetChange: (subscriptionId: number, value: number) => void;
  onToggleLock: (subscriptionId: number) => void;
}

const VIEWBOX_W = 100;
const VIEWBOX_H = 56;
const CX = 50;
const CY = 50;
const R = 40;
const ARC_LENGTH = Math.PI * R;

function describeArc(cx: number, cy: number, r: number): string {
  return `M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`;
}

const GaugeCard = ({
  entry,
  onPercentageChange,
  onTotalChange,
  onStableChange,
  onAssetChange,
  onToggleLock,
}: CardProps) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const pctDelta = entry.currentPercentage - entry.originalPercentage;

  const stablePortion =
    entry.currentTotalBalance > 0
      ? (entry.currentStableBalance / entry.currentTotalBalance) * 100
      : 50;

  const fillLength = (entry.currentPercentage / 100) * ARC_LENGTH;
  const stableFillLength = (stablePortion / 100) * fillLength;

  const handleInteraction = useCallback(
    (clientX: number, clientY: number) => {
      if (entry.isDisabled || entry.isLocked) return;
      const svg = svgRef.current;
      if (!svg) return;

      const rect = svg.getBoundingClientRect();
      // Convert screen coords to SVG viewBox coords
      const scaleX = VIEWBOX_W / rect.width;
      const scaleY = VIEWBOX_H / rect.height;
      const svgX = (clientX - rect.left) * scaleX;
      const svgY = (clientY - rect.top) * scaleY;

      // Calculate angle from center of the arc
      const dx = svgX - CX;
      const dy = -(svgY - CY); // flip Y since SVG Y goes down

      let angle = Math.atan2(dy, dx); // radians, 0=right, PI=left
      // Clamp to the semi-circle range [0, PI]
      if (angle < 0) angle = 0;
      if (angle > Math.PI) angle = Math.PI;

      // Map angle to percentage: PI (left) = 0%, 0 (right) = 100%
      const pct = ((Math.PI - angle) / Math.PI) * 100;
      onPercentageChange(entry.subscriptionId, Math.max(0, Math.min(100, pct)));
    },
    [
      entry.isDisabled,
      entry.isLocked,
      entry.subscriptionId,
      onPercentageChange,
    ],
  );

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (entry.isDisabled || entry.isLocked) return;
      e.preventDefault();
      handleInteraction(e.clientX, e.clientY);

      const onMouseMove = (e: MouseEvent) =>
        handleInteraction(e.clientX, e.clientY);
      const onMouseUp = () => {
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
      };
      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    },
    [entry.isDisabled, entry.isLocked, handleInteraction],
  );

  const arcPath = describeArc(CX, CY, R);

  const cardClass = `card-gauge-card ${entry.isDisabled ? 'disabled' : ''} ${
    entry.isLocked ? 'locked' : ''
  }`;

  return (
    <div className={cardClass}>
      <div className="card-gauge-card-header">
        <div className="card-gauge-name-row">
          <AssetIcon
            baseAsset={entry.baseAsset}
            width={16}
            height={16}
            style={{ marginBottom: 0, borderRadius: 100 }}
          />
          <span className="card-gauge-name">{entry.botFriendlyName}</span>
        </div>
        <button
          className="card-gauge-lock"
          onClick={() => onToggleLock(entry.subscriptionId)}
        >
          {entry.isLocked ? (
            <FaLock color={colors.orange} size={10} />
          ) : (
            <FaLockOpen color={colors.green} size={10} />
          )}
        </button>
      </div>

      <div className="card-gauge-gauge-wrapper">
        <svg
          className="card-gauge-gauge-svg"
          viewBox={`0 0 ${VIEWBOX_W} ${VIEWBOX_H}`}
          ref={svgRef}
          onMouseDown={handleMouseDown}
        >
          {/* Background arc */}
          <path
            d={arcPath}
            fill="none"
            stroke="rgba(255, 255, 255, 0.08)"
            strokeWidth="8"
            strokeLinecap="round"
          />
          {/* Stable fill (from left) */}
          <path
            d={arcPath}
            fill="none"
            stroke="#4d9a79"
            strokeWidth="8"
            strokeLinecap="round"
            strokeDasharray={ARC_LENGTH}
            strokeDashoffset={ARC_LENGTH - stableFillLength}
          />
          {/* Asset fill (continues after stable) */}
          <path
            d={arcPath}
            fill="none"
            stroke="#00B061"
            strokeWidth="8"
            strokeLinecap="round"
            strokeDasharray={ARC_LENGTH}
            strokeDashoffset={ARC_LENGTH - fillLength}
          />
        </svg>
        <div className="card-gauge-gauge-label">
          <span className="card-gauge-pct">
            {formatNumber(entry.currentPercentage, 1)}%
          </span>
          {Math.abs(pctDelta) >= 0.01 && (
            <span
              className="card-gauge-delta"
              style={{
                color: pctDelta > 0 ? colors.green : colors.red,
              }}
            >
              {pctDelta > 0 ? '+' : ''}
              {formatNumber(pctDelta, 1)}%
            </span>
          )}
        </div>
      </div>

      <div className="card-gauge-inputs-wrapper">
        <DistributionInputs
          entry={entry}
          onTotalChange={onTotalChange}
          onStableChange={onStableChange}
          onAssetChange={onAssetChange}
          onPercentageChange={onPercentageChange}
        />
      </div>
    </div>
  );
};

export default CardGauges;
